import {
  Heading,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import actions, { DisplayFullOrganization } from "../../actions";
import { useEffect, useMemo, useState } from "react";
import { useDialog } from "src/Common/Dialog";
import { useStripe } from "../hooks/useStripe";
import { BillingCard } from "./BillingCard";
import { PlansSection } from "./PlansSection";
import { InvoicesSection } from "./InvoicesSection";
import { useLoading } from "../hooks/form";
import { KYDInvoice } from "@common/interfaces/KYDBilling";
import PaymentStatusBadge, { BillingStatus } from "./PaymentStatusBadge";
import { KYDScope } from "@common/interfaces/ICognito";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import type { PaymentMethod, SetupIntent } from "@stripe/stripe-js";

const Billing = ({
  permissions,
  organization,
  refetchOrganization,
  isLoadingOrganization = false,
}: {
  permissions: KYDScope[];
  organization: DisplayFullOrganization;
  refetchOrganization?: () => Promise<void>;
  isLoadingOrganization?: boolean;
}) => {
  const [invoices, setInvoices] = useState<KYDInvoice[]>([]);
  const { stripe: stripeObject } = useStripe({ organization });
  const toast = useToast();
  const dialog = useDialog();
  const [localLoading, setLocalLoading] = useLoading({
    billing: false,
    plans: false,
    invoices: false,
  });
  const subscriptions = organization?.subscriptions || [];

  const subscription = useMemo(() => {
    if (!organization?.subscriptions?.length) {
      return null;
    }

    return organization.subscriptions[0];
  }, [organization?.subscriptions]);

  const status: BillingStatus = useMemo(() => {
    if (!subscription) {
      return null;
    }

    return subscription.status;
  }, [subscription]);

  const handleBillingSave = async (paymentMethod: PaymentMethod) => {
    if (!stripeObject) {
      toast({
        title: "Stripe not ready",
        description: "Please wait a moment and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setLocalLoading("billing", true);

      const returnUrl =
        typeof window !== "undefined"
          ? window.location.href
          : "https://app.kydlabs.com";

      const { setup_intent } = await actions.ADMIN.createBillingSetupIntent({
        payment_method_id: paymentMethod.id,
        return_url: returnUrl,
      });

      let confirmedSetupIntent: SetupIntent | undefined = setup_intent;

      if (setup_intent?.status !== "succeeded") {
        const clientSecret = setup_intent?.client_secret;
        if (!clientSecret) {
          throw new Error("Unable to initiate payment method setup.");
        }

        const { error, setupIntent } = await stripeObject.confirmCardSetup(
          clientSecret,
          {
            payment_method: paymentMethod.id,
            return_url: returnUrl,
          }
        );

        if (error) {
          throw new Error(error.message);
        }

        confirmedSetupIntent = setupIntent;
      }

      const setupIntentPaymentMethod =
        confirmedSetupIntent?.payment_method || paymentMethod.id;
      const paymentMethodId =
        typeof setupIntentPaymentMethod === "string"
          ? setupIntentPaymentMethod
          : setupIntentPaymentMethod?.id;

      if (!paymentMethodId) {
        throw new Error("Stripe did not return a payment method.");
      }

      await actions.ADMIN.updateBilling({
        id: paymentMethodId,
        brand: paymentMethod.card?.brand || "card",
        last4: paymentMethod.card?.last4 || "",
        exp_month: paymentMethod.card?.exp_month || 0,
        exp_year: paymentMethod.card?.exp_year || 0,
      });

      toast({
        title: "Payment Method Updated",
        description: `Your payment method has been updated`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await refetchOrganization?.();
    } catch (err: any) {
      console.error(err);
      dialog({ text: err.message });
    } finally {
      setLocalLoading("billing", false);
    }
  };

  useEffect(() => {
    setLocalLoading("invoices", true);
    actions.ADMIN.fetchInvoices()
      .then((response) => {
        setInvoices(response.invoices);
      })
      .finally(() => {
        setLocalLoading("invoices", false);
      });
  }, []);

  const hasPaymentMethod = useMemo(() => {
    return !!organization?.settings?.default_payment_method?.payment_method_id;
  }, [organization?.settings?.default_payment_method?.payment_method_id]);

  return (
    <VStack w="100%" spacing={8} py={[2, 6]} maxW={"4xl"} marginX={"auto"}>
      {/* Billing Section */}
      <Stack w="100%" spacing={{ base: "8", lg: "6" }}>
        <Stack
          spacing="4"
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "center" }}
        >
          <Stack spacing="1" w="full" justify="space-between" direction={"row"}>
            <Heading
              size={useBreakpointValue({ base: "xs", lg: "sm" })}
              fontWeight="medium"
            >
              <Text fontSize={"lg"} fontWeight="bold">
                Billing
              </Text>
            </Heading>
            <PaymentStatusBadge
              status={status}
              activeUntilDate={subscription?.current_period_end}
            />
          </Stack>
        </Stack>
        <Stack w="full">
          <BillingCard
            permissions={permissions}
            stripeObject={stripeObject}
            isLoading={isLoadingOrganization}
            onBillingSave={handleBillingSave}
            isSaving={isLoadingOrganization || localLoading.billing}
            paymentMethod={
              organization?.settings?.default_payment_method?.payment_method_id
                ? {
                    brand: organization?.settings.default_payment_method.brand,
                    last4: organization?.settings.default_payment_method.last4,
                    auto_pay: false,
                    next_payment_date: subscription?.next_billing_date,
                  }
                : undefined
            }
            status={status}
          />
        </Stack>
      </Stack>
      {/* Plans Section */}
      {hasPaymentMethod && (
        <PlansSection
          subscriptions={subscriptions || []}
          isLoading={isLoadingOrganization}
        />
      )}
      {/* Invoices Section */}
      {hasPaymentMethod && (
        <InvoicesSection
          invoices={invoices}
          isLoading={localLoading.invoices}
        />
      )}
    </VStack>
  );
};

export default Billing;
